import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/ui/Sidebar';

import Button from '../../components/ui/Button';
import MetricCard from './components/MetricCard';
import CatalogCard from './components/CatalogCard';
import FilterBar from './components/FilterBar';
import EmptyState from './components/EmptyState';
import CatalogCardSkeleton from './components/CatalogCardSkeleton';
import ErrorState from '../../components/ui/ErrorState';
import Toast from '../../components/ui/Toast';
import { catalogService } from '../../services/catalogService';
import { activityService } from '../../services/activityService';
import { menuService } from '../../services/menuService';
import { useAuth } from '../../contexts/AuthContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading, activeCompanyId } = useAuth();
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [catalogs, setCatalogs] = useState([]);
  const [menuNodeCount, setMenuNodeCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [toasts, setToasts] = useState([]);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!authLoading && user && activeCompanyId) {
      fetchData();
    }
    return () => {
      if (abortControllerRef?.current) {
        abortControllerRef?.current?.abort();
        abortControllerRef.current = null;
      }
    };
  }, [authLoading, user, activeCompanyId]);

  const fetchData = async () => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setError(null);
      const [catalogsData, menuTree] = await Promise.all([
        catalogService?.getCatalogs(activeCompanyId),
        activeCompanyId
          ? menuService.getMenuTree(activeCompanyId).catch(() => [])
          : Promise.resolve([]),
      ]);
      if (controller?.signal?.aborted) return;
      const catalogIds = (catalogsData || []).map((c) => c.id);
      const previewImagesMap = await catalogService?.getCatalogPreviewImages(catalogIds).catch(() => ({}));
      if (controller?.signal?.aborted) return;
      const catalogsWithPreviews = (catalogsData || []).map((c) => ({
        ...c,
        previewImages: previewImagesMap[c.id] || [],
      }));
      setCatalogs(catalogsWithPreviews);
      const countNodes = (nodes) =>
        nodes.reduce((sum, n) => sum + 1 + countNodes(n.children || []), 0);
      setMenuNodeCount(countNodes(menuTree || []));
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error fetching dashboard data:', err);
      setError(err?.message || t('dashboard.error.loadError'));
    } finally {
      if (!controller?.signal?.aborted) {
        setLoading(false);
      }
    }
  };

  const filteredCatalogs = useMemo(() => {
    // Always exclude archived catalogs from the dashboard
    let filtered = catalogs?.filter((cat) => cat?.status !== 'archived');

    if (statusFilter !== 'all') {
      filtered = filtered?.filter((cat) => cat?.status === statusFilter);
    }

    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter((cat) =>
      cat?.title?.toLowerCase()?.includes(query)
      );
    }

    if (dateRange !== 'all') {
      const now = new Date();
      filtered = filtered?.filter((cat) => {
        const catDate = new Date(cat.createdAt);
        switch (dateRange) {
          case 'today':
            return catDate?.toDateString() === now?.toDateString();
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return catDate >= weekAgo;
          case 'month':
            return catDate?.getMonth() === now?.getMonth() && catDate?.getFullYear() === now?.getFullYear();
          case 'quarter':
            const quarter = Math.floor(now?.getMonth() / 3);
            const catQuarter = Math.floor(catDate?.getMonth() / 3);
            return catQuarter === quarter && catDate?.getFullYear() === now?.getFullYear();
          case 'year':
            return catDate?.getFullYear() === now?.getFullYear();
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [catalogs, statusFilter, dateRange, searchQuery]);

  const metrics = useMemo(() => {
    const activeCatalogs = catalogs?.filter((cat) => cat?.status !== 'archived');
    const total = activeCatalogs?.length || 0;
    const published = activeCatalogs?.filter((cat) => cat?.status === 'published')?.length;
    const draft = activeCatalogs?.filter((cat) => cat?.status === 'draft')?.length;

    return {
      totalCatalogs: total,
      publishedCatalogs: published,
      draftCatalogs: draft
    };
  }, [catalogs]);


  const handleDeleteCatalog = async (catalogId) => {
    try {
      setDeletingId(catalogId);

      // Log activity before deleting (non-fatal: FK cascade handles cleanup)
      try {
        await activityService?.logActivity({
          type: 'delete',
          action: 'deleted',
          catalogId: catalogId,
          catalogTitle: catalogs?.find(c => c?.id === catalogId)?.title
        });
      } catch (logErr) {
        console.warn('Activity log failed (non-fatal):', logErr?.message);
      }

      await catalogService?.deleteCatalog(catalogId);
      setCatalogs(catalogs?.filter(cat => cat?.id !== catalogId));
      setToasts(prev => [...prev, { id: Date.now(), type: 'success', message: t('dashboard.deleteConfirm.successMessage') }]);
    } catch (err) {
      console.error('Error deleting catalog:', err);
      setToasts(prev => [...prev, { id: Date.now(), type: 'error', message: t('dashboard.error.title') }]);
    } finally {
      setDeletingId(null);
    }
  };

  // Called by CatalogCard AFTER it already successfully archived the catalog
  const handleArchiveCatalog = (catalogId) => {
    setCatalogs(prev => prev?.filter(cat => cat?.id !== catalogId));
    setToasts(prev => [...prev, { id: Date.now(), type: 'success', message: t('dashboard.catalogCard.toasts.archiveSuccess') }]);
  };

  const handleStatusChange = () => {
    fetchData();
  };

  const handleEditCatalog = (catalog) => {
    navigate(`/catalog-editor?id=${catalog?.id}`);
  };

  const handleViewCatalog = (catalog) => {
    navigate(`/catalog-viewer?id=${catalog?.id}`);
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setDateRange('all');
    setSearchQuery('');
  };

  const hasActiveFilters = statusFilter !== 'all' || dateRange !== 'all' || searchQuery?.trim() !== '';

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
        <div className={`transition-all duration-250 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]'}`}>
          <div className="p-4 md:p-6 lg:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
              <div className="flex-1">
                <div className="h-10 w-48 bg-muted animate-pulse rounded-md mb-2" />
                <div className="h-5 w-96 bg-muted animate-pulse rounded-md" />
              </div>
              <div className="h-12 w-48 bg-muted animate-pulse rounded-md" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
              {[1, 2, 3, 4]?.map((i) => (
                <div key={i} className="bg-card rounded-lg border border-border p-6">
                  <div className="h-5 w-32 bg-muted animate-pulse rounded-md mb-3" />
                  <div className="h-8 w-24 bg-muted animate-pulse rounded-md mb-2" />
                  <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                  {[1, 2, 3, 4]?.map((i) => (
                    <CatalogCardSkeleton key={i} />
                  ))}
                </div>
              </div>
              <div className="lg:col-span-1">
                <div className="bg-card rounded-lg border border-border p-4">
                  <div className="h-6 w-32 bg-muted animate-pulse rounded-md mb-4" />
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5]?.map((i) => (
                      <div key={i} className="h-16 bg-muted animate-pulse rounded-md" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
        <div className={`transition-all duration-250 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]'}`}>
          <div className="flex items-center justify-center min-h-screen">
            <ErrorState
              title={t('dashboard.error.title')}
              message={error}
              onRetry={fetchData}
              retryText={t('dashboard.error.retry')}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
      <div className={`transition-all duration-250 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]'}`}>
        <div className="p-4 md:p-6 lg:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">{t('dashboard.title')}</h1>
              <p className="text-sm md:text-base text-muted-foreground">
                {t('dashboard.welcome')}, {userProfile?.full_name || user?.email}
              </p>
            </div>
            <Button
              variant="default"
              size="lg"
              onClick={() => navigate('/pdf-upload')}
              iconName="Plus"
              iconPosition="left"
              className="w-full sm:w-auto"
            >
              {t('dashboard.createCatalog')}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            <MetricCard
              title={t('dashboard.metrics.totalCatalogs')}
              value={metrics?.totalCatalogs}
              icon="BookOpen"
              iconColor="bg-accent/10 text-accent"
              trend="neutral"
              trendValue={0}
            />

            <MetricCard
              title={t('dashboard.metrics.published')}
              value={metrics?.publishedCatalogs}
              icon="BookOpen"
              iconColor="bg-green-500/10 text-green-600"
              trend="neutral"
              trendValue={0}
            />

            <MetricCard
              title={t('dashboard.metrics.draft')}
              value={metrics?.draftCatalogs}
              icon="BookOpen"
              iconColor="bg-yellow-500/10 text-yellow-600"
              trend="neutral"
              trendValue={0}
            />

            <MetricCard
              title={t('dashboard.metrics.menuItems')}
              value={menuNodeCount}
              icon="Menu"
              iconColor="bg-purple-500/10 text-purple-600"
              trend="neutral"
              trendValue={0}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 md:gap-8">
            <div>
              <FilterBar
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                dateRange={dateRange}
                setDateRange={setDateRange}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
              />


              {filteredCatalogs?.length === 0 ? (
                <EmptyState
                  hasFilters={hasActiveFilters}
                  onClearFilters={handleClearFilters}
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {filteredCatalogs?.map((catalog) => (
                    <CatalogCard
                      key={catalog?.id}
                      catalog={catalog}
                      onDelete={handleDeleteCatalog}
                      onEdit={handleEditCatalog}
                      onView={handleViewCatalog}
                      onStatusChange={handleStatusChange}
                      onArchive={handleArchiveCatalog}
                      isDeleting={deletingId === catalog?.id}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {toasts?.map((toast) => (
          <Toast
            key={toast?.id}
            message={toast?.message}
            type={toast?.type}
            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
          />
        ))}
      </div>
    </div>
  );

};

export default Dashboard;